/**
 * Pagination concern for DataViewTable - both modes.
 *
 * Server-paginated (`serverPagination` prop supplied): the parent owns the
 * page state and refetches on change; this hook passes the page/pageSize
 * through and renders `sortedData` as-is (the parent already sliced it).
 * Client-paginated (default): the hook owns page/rowsPerPage state, slices
 * the sorted rows locally and resets to page 0 on new data / filter
 * changes. Server callers own their resets - `data` arrives on every page
 * change, so resetting here would clobber the user's paging.
 *
 * Extracted as a pure structural move - state, effects and memo
 * dependency arrays are byte-identical to the inline originals.
 */
import { useEffect, useMemo, useState } from 'react'
import type { ExceptionConfig } from '../../../config/exceptions/types'

/**
 * Server-driven pagination: the parent owns the page state and refetches
 * on change; this table renders `data` as-is (one page) and drives the
 * pagination control from the supplied props instead of slicing the
 * dataset client-side. When set, the chip-strip / sort / toggle still
 * filter the loaded page only - configs that need cross-page filtering
 * should stay with client pagination (default).
 */
export interface ServerPaginationConfig {
  page: number               // 0-based
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  rowsPerPageOptions?: number[]
}

export function useTablePagination({
  sortedData,
  data,
  config,
  serverPagination,
  externalFilterValues,
  response,
}: {
  sortedData: Record<string, any>[]
  data: Record<string, any>[]
  config: ExceptionConfig
  serverPagination?: ServerPaginationConfig
  externalFilterValues?: Record<string, any>
  response?: Record<string, any>
}) {
  const isServerPaginated = !!serverPagination
  const [clientPage, setClientPage] = useState(0)
  const [clientRowsPerPage, setClientRowsPerPage] = useState(
    config.clientDefaultPageSize ?? 50,
  )
  const page = isServerPaginated ? serverPagination!.page : clientPage
  const rowsPerPage = isServerPaginated ? serverPagination!.pageSize : clientRowsPerPage
  const setPage = isServerPaginated ? serverPagination!.onPageChange : setClientPage
  const setRowsPerPage = isServerPaginated
    ? serverPagination!.onPageSizeChange
    : setClientRowsPerPage

  useEffect(() => {
    // Client pagination owns its own page state, so resetting on a new
    // data payload makes sense (filter swap, refetch, etc.). Server
    // pagination is parent-driven and `data` arrives every page change;
    // calling setPage(0) here would clobber the user's paging back to
    // the start on every fetch.
    if (!isServerPaginated) {
      setClientPage(0)
    }
  }, [config.filters, data, isServerPaginated, externalFilterValues])

  // Reset client page whenever the route-lifted filter values change so
  // a sub-axis flip on sales-compare etc. lands back on page 1.
  useEffect(() => {
    if (externalFilterValues === undefined) return
    if (!isServerPaginated) setClientPage(0)
  }, [externalFilterValues, isServerPaginated])

  // Server-paginated: parent already sliced the page, render the rows
  // straight through. Client-paginated: fall back to the local slice so
  // sorting + chip filters stay snappy on the loaded set.
  const displayedRows = useMemo(
    () =>
      isServerPaginated
        ? sortedData
        : sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedData, page, rowsPerPage, isServerPaginated]
  )

  // Server pagination: always show the control so the user can advance
  // pages even on the very first page. Client pagination: only show
  // once the loaded set spills past the smallest rows-per-page option.
  const showPagination = isServerPaginated
    ? serverPagination!.total > 0
    : sortedData.length > 25

  const serverTotal = typeof response?.total === 'number' ? response.total : null
  // The truncation warning belongs to the legacy "fetch up to N rows then
  // paginate client-side" path. With server pagination the page control
  // *is* the truth, so suppress it.
  const isTruncated =
    !isServerPaginated && serverTotal !== null && serverTotal > data.length

  return {
    isServerPaginated,
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    displayedRows,
    showPagination,
    serverTotal,
    isTruncated,
  }
}
